import {
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { filter } from 'rxjs/operators';

import { PortalConfig, PortalCredential, PortalType } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import { BasePortalCredentialControl } from '../../controls/base-portal-credential.control';
import { CredentialValidatorService } from '../../services/credential-validator.service';
import { FormFieldLabelComponent } from '../../../components/legacy/form/form-field/form-field-label/form-field-label.component';
import { FormFieldComponent } from '../../../components/legacy/form/form-field/form-field.component';
import { AppInputDirective } from '../../../components/legacy/form/controls/input/input.directive';

@Component({
  selector: 'app-immoscout-credential-details',
  templateUrl: './immoscout-credential-details.component.html',
  styleUrls: ['./immoscout-credential-details.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImmoscoutCredentialsDetailsComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormFieldLabelComponent,
    FormFieldComponent,
    AppInputDirective,
    ImmomioButtonComponent
  ]
})
export class ImmoscoutCredentialsDetailsComponent
  extends BasePortalCredentialControl
  implements OnInit
{
  #destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private credentialValidatorService = inject(CredentialValidatorService);

  public readonly portalConfig = input<PortalConfig>(undefined);
  public readonly credential = input<PortalCredential>(undefined);
  readonly getIs24URL = output();

  public includeChannel: boolean;

  constructor() {
    super();

    this.form = this.fb.group({
      name: ['', Validators.required],
      properties: this.fb.group({
        verificationCode: ['', Validators.required]
      })
    });
  }

  public ngOnInit() {
    this.includeChannel =
      this.portalConfig().portalId === PortalType.IMMOBILIENSCOUT24_GC_DE;

    if (this.includeChannel) {
      this.propertiesControl.addControl(
        'channel',
        new FormControl('', [Validators.required])
      );
    }

    const credentialValue = this.credential();
    if (credentialValue) {
      this.form.patchValue(credentialValue);
    }

    this.form.valueChanges.subscribe(credential => {
      this.value = { ...credential };

      this.onChange(this.value);
      this.onTouch();
    });

    this.credentialValidatorService.validationError$
      .pipe(
        filter(portal => !!portal),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(portal =>
        this.setCredentialValidationError(
          this.portalConfig().validationControl || 'password',
          portal === this.credential().portal
        )
      );
  }

  public onGetIs24URL() {
    this.getIs24URL.emit();
  }
}
